import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FaBuilding, FaEnvelope, FaFileInvoiceDollar } from "react-icons/fa";

import { Wizard, WizardStep } from "shared/components/wizard/Wizard";
import wizardGroupPayStyles from "./wizardGroupPayStyles";

import { useGetRegistrosTabelaCondominioGroupPay } from "hooks/condominio/useGetRegistrosTabelaCondominioGroupPay";
import { usePostAtivarCondominioGroupPay } from "hooks/groupPay/usePostAtivarCondominioGroupPay";
import { useGetStatusWizardGroupPay } from "hooks/groupPay/useGetStatusWizardGroupPay";
import { usePutStatusWizardGroupPay } from "hooks/groupPay/usePutStatusWizadGroupPay";
import { usePutInativarConfiguarcaoBuscaEmail } from "hooks/groupPay/usePutInativarConfiguarcaoBuscaEmail";
import { useGetEmailRedirecionamento } from "hooks/groupPay/useGetEmailRedirecionamento";

import ConfigurarBuscaNFEmail from "modules/groupPay/configuracaoGroupPay/configurarBuscaNFEmail/ConfigurarBuscaNFEmail";
import AtivacaoCondominios from "modules/groupPay/configuracaoGroupPay/AtivacaoCondominios";
import BuscaExternaNf from "modules/groupPay/configuracaoGroupPay/BuscaExternaNf";
import StandardModalBemVindoPay from "../standardModal/StandardModalBemVindoPay";
import { Condominio } from "modules/groupPay/configuracaoGroupPay/ModalReplicarAcesso";

import { RequestCondominiosPaginados } from "types/request/RequestCondominiosPaginados";
import { settarTourFalse } from "hooks/usuario/usuarioTourFuctions";
import { usePutInativarConfiguarcaoBuscaExterna } from "hooks/groupPay/usePutInativarConfiguarcaoBuscaExterna";

type FormData = {
  condominiosIds: number[];
  emailPrefeitura?: string;
  senhaPrefeitura?: string;
};

export const WizardGroupPay: React.FC = () => {
  const classes = wizardGroupPayStyles();
  const { register, getValues, setValue } = useForm<FormData>({
    defaultValues: { condominiosIds: [] },
  });

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [searchCondominio, setSearchCondominio] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [sortString, setSortString] = useState("");
  const [condominiosSelecionados, setCondominiosSelecionados] = useState<Condominio[]>([]);
  const [idsCondominiosSelecionados, setIdsCondominiosSelecionados] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [condominiosLocais, setCondominiosLocais] = useState<Condominio[]>([]);
  const initializedRef = useRef(false);
  const ativacaoRealizadaRef = useRef(false);
  const [showModal, setShowModal] = useState(() => {
    const usuarioTour = localStorage.getItem("usuarioTour");
    const deveExibir = usuarioTour ? JSON.parse(usuarioTour).deveExibirModalBemVindoGroupPay : null;

    return deveExibir || deveExibir === null;
  });

  const [isStep3Configured, setIsStep3Configured] = useState(false);

  const { mutateAsync: ativarCondominiosAsync } = usePostAtivarCondominioGroupPay();
  const { mutateAsync: atualizarStatusWizard } = usePutStatusWizardGroupPay();
  const { mutateAsync: inativarConfiguracaoEmailAsync } = usePutInativarConfiguarcaoBuscaEmail();
  const { mutateAsync: inativarConfiguracaoExternaAsync } = usePutInativarConfiguarcaoBuscaExterna();

  const { data: statusWizardData } = useGetStatusWizardGroupPay();
  const { data: emailRedirecionamentoResponse } = useGetEmailRedirecionamento();
  const [currentStep, setCurrentStep] = useState(0);
  const [maxStepAllowed, setMaxStepAllowed] = useState(0);

  const [isBuscaExternaAtiva, setIsBuscaExternaAtiva] = useState(true);
  const [isBuscaPorEmailAtiva, setIsBuscaPorEmailAtiva] = useState(true);

  const groupPayEmail = emailRedirecionamentoResponse?.payload || "";

  const params: RequestCondominiosPaginados = {
    size: 99999,
    page: paginaAtual - 1,
    sort: sortString,
    nomeCondominio: searchCondominio,
  };

  const { data: condominiosPaginados, isFetching } = useGetRegistrosTabelaCondominioGroupPay(params);

  const condominiosInativosParaExibicao = useMemo(() => {
    if (!condominiosLocais.length) return [];
    return condominiosLocais.filter((cond: Condominio) => !cond.possuiGroupPayAtivo);
  }, [condominiosLocais]);

  useEffect(() => {
    if (condominiosPaginados?.content) {
      setCondominiosLocais(condominiosPaginados.content);
    }
  }, [condominiosPaginados]);

  useEffect(() => {
    if (statusWizardData?.payload?.status) {
      const status = statusWizardData.payload.status as string;

      const statusToStep: Record<string, number> = {
        ATIVACAO_CONDOMINIO: 0,
        BUSCA_EXTERNA: 1,
        BUSCA_EMAIL: 2,
        CONFIGURADO: 3,
        FIM: 4,
      };

      const step = statusToStep[status] ?? 0;
      setCurrentStep(step);
      setMaxStepAllowed(step);
    }
  }, [statusWizardData]);

  useEffect(() => {
    setValue("condominiosIds", idsCondominiosSelecionados.filter(Boolean));
  }, [idsCondominiosSelecionados, setValue]);

  useEffect(() => {
    if (condominiosLocais.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      setCondominiosSelecionados([]);
      setIdsCondominiosSelecionados([]);
    }
  }, [condominiosLocais]);

  useEffect(() => {
    if (currentStep === 0 && ativacaoRealizadaRef.current) {
      const idsInativos = condominiosInativosParaExibicao.map((cond: Condominio) => cond.condominioId);
      const idsParaManter = idsCondominiosSelecionados.filter((id: number) => idsInativos.includes(id));
      
      if (idsParaManter.length !== idsCondominiosSelecionados.length) {
        setIdsCondominiosSelecionados(idsParaManter);
        setCondominiosSelecionados((prev) => 
          prev.filter((cond: Condominio) => idsParaManter.includes(cond.condominioId))
        );
      }
      
      ativacaoRealizadaRef.current = false;
    }
  }, [currentStep, condominiosInativosParaExibicao, idsCondominiosSelecionados]);

  const isStep1Valid = idsCondominiosSelecionados.length > 0;

  const isStep2Valid = useMemo(() => {
    if (!isBuscaExternaAtiva) return true;
    const condominiosAtivos = condominiosLocais.filter((cond: Condominio) => cond.possuiGroupPayAtivo);
    return condominiosAtivos.some((cond: Condominio) => cond.possuiGroupPayConfigurado === true);
  }, [condominiosLocais, isBuscaExternaAtiva]);

  const isStep3Valid = useMemo(() => {
    if (!isBuscaPorEmailAtiva) return true;
    return isStep3Configured;
  }, [isStep3Configured, isBuscaPorEmailAtiva]);

  const isStepEnabled = (stepIndex: number): boolean => {
    return stepIndex <= maxStepAllowed;
  };

  const handleConfiguracaoSalva = (condominioId: number) => {
    setCondominiosLocais((prevCondominios) =>
      prevCondominios.map((cond) =>
        cond.condominioId === condominioId ? { ...cond, possuiGroupPayConfigurado: true } : cond
      )
    );
  };

  const handleConfiguracaoEmailSalva = (isConfigurado: boolean) => {
    setIsStep3Configured(isConfigurado);
  };

  const wizardSteps: WizardStep[] = [
    {
      id: "ativacao-condominios",
      label: "1. Ativação dos condomínios",
      icon: <FaBuilding color="white" />,
      inactiveIcon: <FaBuilding color="#7BB992" />,
      content: (
        <AtivacaoCondominios
          condominios={{ content: condominiosInativosParaExibicao }}
          searchCondominio={searchCondominio}
          setSearchCondominio={setSearchCondominio}
          condominiosSelecionados={condominiosSelecionados}
          setCondominiosSelecionados={setCondominiosSelecionados}
          idsCondominiosSelecionados={idsCondominiosSelecionados}
          setIdsCondominiosSelecionados={setIdsCondominiosSelecionados}
          setSortString={setSortString}
          isFetching={isFetching}
          register={register}
          setValue={setValue}
          getValues={getValues}
        />
      ),
      isValid: isStep1Valid,
      disabled: !isStepEnabled(0),
    },
    {
      id: "busca-externa",
      label: "2. Busca externa",
      icon: <FaFileInvoiceDollar color="white" />,
      inactiveIcon: <FaFileInvoiceDollar color="#7BB992" />,
      content: (
        <BuscaExternaNf
          condominios={{ content: condominiosLocais }}
          searchCondominio={searchCondominio}
          setSearchCondominio={setSearchCondominio}
          onConfiguracaoSalva={handleConfiguracaoSalva}
          onToggleBuscaExterna={setIsBuscaExternaAtiva}
        />
      ),
      isValid: isStep2Valid,
      disabled: !isStepEnabled(1),
    },
    {
      id: "busca-email",
      label: "3. Busca por e-mail",
      icon: <FaEnvelope color="white" />,
      inactiveIcon: <FaEnvelope color="#7BB992" />,
      content: (
        <ConfigurarBuscaNFEmail
          isInsideWizard={true}
          onConfiguracaoSalva={handleConfiguracaoEmailSalva}
          onToggleBuscaPorEmail={setIsBuscaPorEmailAtiva}
        />
      ),
      isValid: isStep3Valid,
      disabled: !isStepEnabled(2),
    },
  ];

  const wizardStatusMap: Record<number, string> = {
    0: "ATIVACAO_CONDOMINIO",
    1: "BUSCA_EXTERNA",
    2: "BUSCA_EMAIL",
    3: "CONFIGURADO",
    4: "FIM",
  };

  const handleStepChange = async (currentStepIndex: number, direction: "next" | "back", targetStep?: number) => {
    if (targetStep !== undefined && targetStep < currentStepIndex && targetStep <= maxStepAllowed) {
      setFormData((prev) => ({
        ...prev,
        [`groupPay_step_${currentStepIndex}`]: {
          stepId: wizardSteps[currentStepIndex]?.id,
          timestamp: new Date().toISOString(),
          direction: "navigation",
          data: getValues(),
        },
      }));
      return;
    }

    if (currentStepIndex === 0 && direction === "next") {
      setIsProcessing(true);
      try {
        if (idsCondominiosSelecionados.length > 0) {
          let possuiCnpjVazio = condominiosSelecionados.some((cond: Condominio) => !cond.cnpj)
          if (possuiCnpjVazio) {
            throw toast.error("Não é possível ativar condomínios sem CNPJ");
          } else {
            let body = { ids: idsCondominiosSelecionados };
            await ativarCondominiosAsync(body).then(() => {
              ativacaoRealizadaRef.current = true;
              toast.success(
                <div>
                  <strong>1° Etapa - Condomínios ativados!</strong>
                  <div>Os condomínios estão ativos no Group Pay.</div>
                </div>
              );
            })
          }
        }
      } catch (error: any) {
        if (error?.response?.status === 500) {
          toast.error("Erro ao processar condomínios. Verifique os dados e tente novamente.");
        }
        throw error;
      } finally {
        setIsProcessing(false);
      }
    }

    if (direction === "next") {
      try {
        const novoStatus = wizardStatusMap[currentStepIndex + 1] || "CONFIGURADO";
        await atualizarStatusWizard(novoStatus);

        setMaxStepAllowed(Math.max(maxStepAllowed, currentStepIndex + 1));

        if (currentStepIndex === 1 && !isBuscaExternaAtiva) {
          await inativarConfiguracaoExternaAsync();
        }

        if (currentStepIndex === 2 && !isBuscaPorEmailAtiva && groupPayEmail) {
          await inativarConfiguracaoEmailAsync(groupPayEmail);
        }

      } catch (error) {
        throw error;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [`groupPay_step_${currentStepIndex}`]: {
        stepId: wizardSteps[currentStepIndex]?.id,
        timestamp: new Date().toISOString(),
        direction,
        data: getValues(),
      },
    }));
  };

  return (
    <div className={classes.root}>
      <StandardModalBemVindoPay
        showModal={showModal}
        onCloseModal={() => {
          settarTourFalse("deveExibirModalBemVindoGroupPay");
          setShowModal(false);
        }}
      />
      <Wizard
        steps={wizardSteps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onStepChange={handleStepChange}
        showCancel={false}
        nextButtonText="Avançar"
        backButtonText="Voltar"
        finishButtonText="Finalizar"
        allowStepNavigation={true}
        nonLinear={true}
        showProgress={false}
        elevation={0}
        orientation="horizontal"
        alternativeLabel
        className="grouppay-wizard"
        style={{ background: "transparent", boxShadow: "none" }}
        showContentHeader={false}
        hideBackButtonOnFirstStep
      />
    </div>
  );
};

export default WizardGroupPay;